import React, { useEffect } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { MenuItem, useToast } from "@chakra-ui/react";
import { AiOutlineCheck } from "react-icons/ai";

import { useUpdatePaymentMethod } from "api/payments/index.js";

/**
 * SetDefaultButton - Component to set a payment method as default, wrapped as a MenuItem Chakra component.
 * @param {SetDefaultButton} props
 * @param {Object} props.paymentMethod - the payment method that this component is responsible for.
 * @param {Object} props.isPayout - if payment method is a payout, it will handle updates differently.
 * @param {Function} props.onComplete - callback function when an action is performed successfully.
 * @returns JSX.Element
 */
const SetDefaultButton = ({ paymentMethod, isPayout, onComplete }) => {
  const { formatMessage } = useIntl();
  const toast = useToast();

  const updateType = isPayout ? { defaultPayout: true } : { defaultPayment: true };

  const {
    mutateAsync: updatePaymentMethod,
    isSuccess: isUpdatePMSuccess,
    isLoading: isUpdatingPM,
    error: updatePMError,
  } = useUpdatePaymentMethod();

  const handleSetDefaultClick = () => {
    updatePaymentMethod({
      id: paymentMethod.id,
      displayString: paymentMethod.display_string,
      ...updateType,
    });
  };

  useEffect(() => {
    if (isUpdatePMSuccess) {
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Payment method set as default successfully",
          description: "Popup description",
        }),
        status: "success",
        isClosable: true,
      });
      onComplete();
    }
  }, [isUpdatePMSuccess, toast, onComplete, formatMessage]);

  useEffect(() => {
    if (updatePMError) {
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Popup title",
        }),
        description: updatePMError.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [updatePMError, toast, formatMessage]);

  return (
    <MenuItem
      icon={<AiOutlineCheck />}
      onClick={(e) => {
        e.stopPropagation();
        handleSetDefaultClick();
      }}
      isDisabled={isUpdatingPM}
    >
      <FormattedMessage defaultMessage="Set as Default" description="button label" />
    </MenuItem>
  );
};

export default SetDefaultButton;
